import { ServiceIndisponibleError, SessionExpireeError } from '../erreurs';

/** Time given to Tahiti to answer one call before the service is deemed unreachable. */
export const DELAI_REQUETE_MS = 30_000;

export interface OptionsHttp {
  /** The `fetch` to use; the global one when omitted (tests bring their own). */
  fetch?: typeof globalThis.fetch;
  /** Timeout of one call, in milliseconds. */
  delaiMs?: number;
}

export interface RequeteHttp {
  methode: 'GET' | 'POST';
  url: string;
  /** Session token, sent as `Authorization: Bearer`. */
  jeton?: string;
  /** JSON body (POST). */
  corps?: unknown;
  /**
   * What a 400, 401 or 403 means for this call. By default the session is no longer
   * accepted (`SessionExpireeError`); the login and the work context override it.
   */
  surRefus?: (statut: number) => Error;
}

export type AppelHttp = (requete: RequeteHttp) => Promise<unknown>;

const refusParDefaut = (): Error => new SessionExpireeError();

/**
 * The single HTTP door of the Tahiti client, and the only place that calls `fetch`.
 * It returns the JSON body of a successful call, still `unknown`: each call validates
 * it with its own Zod schema.
 *
 *   - 401 and 403            -> `SessionExpireeError` (or what `surRefus` says);
 *   - network failure, timeout, other statuses, body that is not JSON
 *                            -> `ServiceIndisponibleError`.
 */
export function creerAppelHttp(options: OptionsHttp = {}): AppelHttp {
  const delaiMs = options.delaiMs ?? DELAI_REQUETE_MS;

  return async (requete) => {
    // Resolved at call time so a test can replace the global `fetch`.
    const appeler = options.fetch ?? globalThis.fetch;
    const controleur = new AbortController();
    const minuteur = setTimeout(() => controleur.abort(), delaiMs);

    try {
      const entetes: Record<string, string> = { Accept: 'application/json' };
      if (requete.corps !== undefined) entetes['Content-Type'] = 'application/json';
      if (requete.jeton !== undefined) entetes.Authorization = `Bearer ${requete.jeton}`;

      let reponse: Response;
      try {
        reponse = await appeler(requete.url, {
          method: requete.methode,
          headers: entetes,
          ...(requete.corps === undefined ? {} : { body: JSON.stringify(requete.corps) }),
          signal: controleur.signal,
        });
      } catch (erreur) {
        const delaiDepasse = controleur.signal.aborted;
        throw new ServiceIndisponibleError(
          delaiDepasse
            ? `Tahiti did not answer within ${delaiMs} ms`
            : `Tahiti is unreachable: ${erreur instanceof Error ? erreur.message : String(erreur)}`,
        );
      }

      if (reponse.status === 400 || reponse.status === 401 || reponse.status === 403) {
        // A 400 only means "refused" when the call says so (login); elsewhere it is a
        // request Tahiti did not understand, i.e. an unexpected answer.
        if (reponse.status !== 400 || requete.surRefus !== undefined) {
          throw (requete.surRefus ?? refusParDefaut)(reponse.status);
        }
      }
      if (!reponse.ok) {
        throw new ServiceIndisponibleError(`Tahiti answered ${reponse.status} to ${requete.url}`);
      }

      try {
        return (await reponse.json()) as unknown;
      } catch {
        throw new ServiceIndisponibleError('Tahiti answered with a body that is not JSON');
      }
    } finally {
      clearTimeout(minuteur);
    }
  };
}
